import { translationRequest } from '../shared/reading_request';
import AudioPlayerService from './chat/audio_player_service';
import { secondaryAudioVoiceOptions } from '../utils/secondaryAudioVoice';

class LessonProgressService {
  constructor() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    this.audioPlayerService = new AudioPlayerService(this.csrfToken);
    this.translationCache = {};

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Audio playback
    document.addEventListener('click', (e) => {
      const audioBtn = e.target.closest('.js-play-phrase-audio');
      if (audioBtn) {
        e.preventDefault();
        this.playAudio(audioBtn);
      }
    });

    // Translation toggle
    document.addEventListener('click', (e) => {
      const translateBtn = e.target.closest('.js-toggle-translation');
      if (translateBtn) {
        e.preventDefault();
        this.toggleTranslation(translateBtn);
      }
    });

    // Save single phrase
    document.addEventListener('click', (e) => {
      const saveBtn = e.target.closest('.js-save-phrase');
      if (saveBtn) {
        e.preventDefault();
        this.savePhrase(saveBtn);
      }
    });

    // Save all phrases
    document.addEventListener('click', (e) => {
      const saveAllBtn = e.target.closest('.js-save-all-btn');
      if (saveAllBtn) {
        e.preventDefault();
        this.saveAllPhrases(saveAllBtn);
      }
    });
  }

  playAudio(element) {
    const phrase = element.dataset.phrase;
    const language = element.dataset.language;

    if (!phrase || !language) {
      console.error('Missing phrase or language data');
      return;
    }

    // Show loading state
    element.classList.add('pulse');
    const icon = element.querySelector('i');
    const originalClass = icon?.className;

    // Japanese follows the selected voice (LAN-326); other languages keep ElevenLabs.
    const ttsOptions = secondaryAudioVoiceOptions({ language, voice: element.dataset.voice, keepRotation: true });

    this.audioPlayerService.fetchAudioData(phrase, language, ttsOptions)
      .then(data => {
        element.classList.remove('pulse');
        this.audioPlayerService.setupPlayer(data.audioUrl, element, data.playbackRate || 1.0);
        this.audioPlayerService.player.play();
      })
      .catch(error => {
        console.error('Error playing audio:', error);
        element.classList.remove('pulse');
        if (icon) icon.className = originalClass;
      });
  }

  async toggleTranslation(element) {
    const listItem = element.closest('.list-group-item');
    const translationDisplay = listItem?.querySelector('.js-translation-display');

    if (!translationDisplay) return;

    // If already visible, hide it
    if (!translationDisplay.classList.contains('d-none')) {
      translationDisplay.classList.add('d-none');
      element.classList.remove('active');
      return;
    }

    const phrase = element.dataset.phrase;
    let translation = element.dataset.translation;

    // If no translation cached, fetch it
    if (!translation || translation === 'null') {
      if (this.translationCache[phrase]) {
        translation = this.translationCache[phrase];
      } else {
        const container = document.getElementById('js-lesson-progress-container');
        const language = container?.dataset.language || 'en';
        const nativeLanguage = container?.dataset.nativeLanguage || 'en';

        translationDisplay.textContent = I18n.t('javascript.lesson_progress.translating');
        translationDisplay.classList.remove('d-none');

        try {
          const response = await translationRequest({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              word: phrase,
              sentence: phrase,
              language: language,
              target_language: nativeLanguage,
              translation_type: 'sentence'
            })
          });

          const data = await response.json();
          translation = data.translation || I18n.t('javascript.lesson_progress.translation_unavailable');
          this.translationCache[phrase] = translation;
          element.dataset.translation = translation;
        } catch (error) {
          console.error('Translation error:', error);
          translation = 'Translation failed';
        }
      }
    }

    translationDisplay.textContent = translation;
    translationDisplay.classList.remove('d-none');
    element.classList.add('active');
  }

  async savePhrase(element) {
    const phrase = element.dataset.phrase;
    let translation = element.dataset.translation;
    const language = element.dataset.language;

    if (!phrase) {
      console.error('Missing phrase data');
      return;
    }

    const plusIcon = element.querySelector('.plus-icon');
    const checkIcon = element.querySelector('.check-icon');

    // If no translation, try to get it first
    if (!translation || translation === 'null') {
      const container = document.getElementById('js-lesson-progress-container');
      const nativeLanguage = container?.dataset.nativeLanguage || 'en';

      try {
        const response = await translationRequest({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            word: phrase,
            sentence: phrase,
            language: language,
            target_language: nativeLanguage,
            translation_type: 'sentence'
          })
        });

        const data = await response.json();
        translation = data.translation || phrase;
      } catch (error) {
        console.error('Translation error:', error);
        translation = phrase;
      }
    }

    // Determine flashcard type based on word count
    const wordCount = phrase.trim().split(/\s+/).length;
    let flashcardType = 'word';
    if (wordCount >= 5) {
      flashcardType = 'sentence';
    } else if (wordCount >= 3) {
      flashcardType = 'phrase';
    }

    const flashcardData = {
      front: phrase,
      back: translation,
      language: language,
      flashcard_type: flashcardType
    };

    try {
      const response = await fetch('/langua/flashcards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        credentials: 'same-origin',
        body: JSON.stringify({ flashcard: flashcardData })
      });

      if (response.ok) {
        // Animate success
        if (plusIcon) {
          plusIcon.style.transition = 'transform 0.3s ease-in-out';
          plusIcon.style.transform = 'rotate(45deg) scale(0)';
        }

        setTimeout(() => {
          if (plusIcon) plusIcon.classList.add('d-none');
          if (checkIcon) {
            checkIcon.classList.remove('d-none');
            checkIcon.style.transform = 'scale(1)';
          }
          element.disabled = true;
        }, 300);
      } else {
        console.error('Failed to save flashcard');
      }
    } catch (error) {
      console.error('Error saving flashcard:', error);
    }
  }

  async saveAllPhrases(element) {
    const phrasesData = element.dataset.phrases;
    const language = element.dataset.language;

    if (!phrasesData) {
      console.error('Missing phrases data');
      return;
    }

    let phrases;
    try {
      phrases = JSON.parse(phrasesData);
    } catch (error) {
      console.error('Invalid phrases data:', error);
      return;
    }

    // Update button state
    const originalText = element.innerHTML;
    element.innerHTML = `<i class="fa fa-spinner fa-spin mr-1"></i> ${I18n.t('javascript.lesson_progress.saving')}`;
    element.disabled = true;

    const container = document.getElementById('js-lesson-progress-container');
    const nativeLanguage = container?.dataset.nativeLanguage || 'en';

    let savedCount = 0;

    for (const phraseData of phrases) {
      const phrase = typeof phraseData === 'string' ? phraseData : (phraseData.phrase || phraseData);
      let translation = typeof phraseData === 'object' ? phraseData.translation : null;

      // Get translation if not available
      if (!translation) {
        try {
          const response = await translationRequest({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              word: phrase,
              sentence: phrase,
              language: language,
              target_language: nativeLanguage,
              translation_type: 'sentence'
            })
          });

          const data = await response.json();
          translation = data.translation || phrase;
        } catch (error) {
          translation = phrase;
        }
      }

      // Determine flashcard type
      const wordCount = phrase.trim().split(/\s+/).length;
      let flashcardType = 'word';
      if (wordCount >= 5) {
        flashcardType = 'sentence';
      } else if (wordCount >= 3) {
        flashcardType = 'phrase';
      }

      try {
        const response = await fetch('/langua/flashcards', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            flashcard: {
              front: phrase,
              back: translation,
              language: language,
              flashcard_type: flashcardType
            }
          })
        });

        if (response.ok) {
          savedCount++;
        }
      } catch (error) {
        console.error('Error saving phrase:', phrase, error);
      }
    }

    // Update button to show success
    element.innerHTML = `<i class="fa fa-check mr-1"></i> ${I18n.t('javascript.lesson_progress.saved_count', { saved: savedCount, total: phrases.length })}`;
    element.classList.remove('btn-primary');
    element.classList.add('btn-success');
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('js-lesson-progress-container');
  if (container) {
    new LessonProgressService();
  }
});

export default LessonProgressService;
